import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { Company, ReferentMutation } from '../../../@types/Company';
import { refreshCompanyData, updateCompanyBranding } from '../../../store/AuthReducer';
import type { RootState } from '../../../store/store';
import {
  canAccessBrandingWizardStep,
  firstIncompleteBrandingWizardStep,
  mergeReferentsPreservingPdfIntroduction,
} from '../../../utils/branding';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import { showToast } from '../../common/Notification';
import ReferentPresentationUploadList from '../../common/ReportGenerator/ReferentPresentationUploadList';
import Spinner from '../../common/Spinner';
import type { AppearanceStepHandle } from './steps/AppearanceStep';
import AppearanceStep from './steps/AppearanceStep';
import type { ColorsStepHandle } from './steps/ColorsStep';
import ColorsStep from './steps/ColorsStep';
import type { CommercialNameStepHandle } from './steps/CommercialNameStep';
import CommercialNameStep from './steps/CommercialNameStep';
import type { LogoStepHandle } from './steps/LogoStep';
import LogoStep from './steps/LogoStep';
import type { ReferentStepHandle } from './steps/ReferentStep';
import ReferentStep from './steps/ReferentStep';
import SummaryStep from './steps/SummaryStep';
import type { TemplateStepHandle } from './steps/TemplateStep';
import TemplateStep from './steps/TemplateStep';

type BrandingDraft = NonNullable<Company['brandingSettings']>;

// Helper to get referents from branding settings or return an initial empty referent
const getReferentsOrDefault = (brandingSettings: Company['brandingSettings']): ReferentMutation[] => {
  // If referents array already exists and has entries, use it
  if (brandingSettings?.referents && brandingSettings.referents.length > 0) {
    return brandingSettings.referents;
  }
  
  // Return empty referent to start with
  return [{
    id: `ref_${Date.now()}_initial`,
    title: '',
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    photoName: undefined,
    hasExternalNewsletter: false,
    externalNewsletterUrl: '',
  }];
};

// Helper to check if referents array has meaningful differences
const referentsEqual = (a: ReferentMutation[] | undefined, b: ReferentMutation[] | undefined): boolean => {
  if (!a && !b) { return true; }
  if (!a || !b) { return false; }
  if (a.length !== b.length) { return false; }
  return a.every((refA, i) => {
    const refB = b[i];
    return refA.title === refB.title &&
           refA.firstname === refB.firstname &&
           refA.lastname === refB.lastname &&
           (refA.email || '') === (refB.email || '') &&
           (refA.phone || '') === (refB.phone || '') &&
           (refA.photoName || '') === (refB.photoName || '') &&
           (refA.hasExternalNewsletter || false) === (refB.hasExternalNewsletter || false) &&
           (refA.externalNewsletterUrl || '') === (refB.externalNewsletterUrl || '') &&
           (refA.userId ?? null) === (refB.userId ?? null);
  });
};

const DEFAULT_COLORS = {
  color1: '#1a237e',
  color2: '#283593',
  color3: '#4db6ac',
};

// Wizard step index of the "Présentation entreprise pour les rapports" step.
const REPORT_UPLOAD_STEP_INDEX = 6;

// Step is managed locally to avoid URL-driven remounts

// arraysShallowEqual helper removed (unused)

function BrandingWizard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const company = useSelector((state: RootState) => state.auth.company) as Company;
  const jwt = useSelector((state: RootState) => state.auth.token);

  const [isSaving, setIsSaving] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [draft, setDraft] = useState<BrandingDraft>({
    color1: DEFAULT_COLORS.color1,
    color2: DEFAULT_COLORS.color2,
    color3: DEFAULT_COLORS.color3,
    blogStructure: 'straight-list',
    defaultTemplate: undefined,
    referents: [],
    emailSubcontractorName: '',
    commercialName: '',
    showLegalInfoOnBlog: true,
  });

  // Initialize draft from company
  useEffect(() => {
    // Authentication first supplies a minimal company; wait for its full API data.
    if (!initialized && company?.id && 'brandingSettings' in company) {
      const base = company.brandingSettings || ({} as BrandingDraft);
      setDraft({
        color1: base.color1 || DEFAULT_COLORS.color1,
        color2: base.color2 || DEFAULT_COLORS.color2,
        color3: base.color3 || DEFAULT_COLORS.color3,
        blogStructure: base.blogStructure || 'straight-list',
        defaultTemplate: base.defaultTemplate,
        referents: getReferentsOrDefault(company.brandingSettings),
        emailSubcontractorName: base.emailSubcontractorName || '',
        commercialName: base.commercialName || '',
        showLegalInfoOnBlog: base.showLegalInfoOnBlog ?? true,
      });
      setInitialized(true);
    }
  }, [company, initialized]);

  // Unsaved changes guard
  const [hasChanges, setHasChanges] = useState(false);
  useEffect(() => {
    if (!company || !company.id) { return; }
    const initial = company.brandingSettings || ({} as BrandingDraft);
    const initialReferents = getReferentsOrDefault(company.brandingSettings);
    const changed = (
      draft.color1 !== (initial.color1 || DEFAULT_COLORS.color1) ||
      draft.color2 !== (initial.color2 || DEFAULT_COLORS.color2) ||
      draft.color3 !== (initial.color3 || DEFAULT_COLORS.color3) ||
      draft.blogStructure !== (initial.blogStructure || 'straight-list') ||
      (draft.defaultTemplate || '') !== (initial.defaultTemplate || '') ||
      (draft.emailSubcontractorName || '') !== (initial.emailSubcontractorName || '') ||
      (draft.commercialName || '') !== (initial.commercialName || '') ||
      (draft.showLegalInfoOnBlog ?? true) !== (initial.showLegalInfoOnBlog ?? true) ||
      !referentsEqual(draft.referents, initialReferents)
    );
    setHasChanges(changed);
  }, [company, draft]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasChanges) { return; }
      e.preventDefault();
      e.returnValue = '';
    };
    if (hasChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
    return () => {};
  }, [hasChanges]);

  const [activeStep, setActiveStep] = useState<number>(0);
  const appliedInitialStepResolution = useRef(false);
  const lastAppliedBrandingStepQuery = useRef<string | null>(null);

  // Local validator for hex colors to allow fallback validation when child ref isn't ready yet
  const isValidHex = (value?: string) => /^#([0-9a-fA-F]{6})$/.test(value || '');

  const commercialNameRef = useRef<CommercialNameStepHandle>(null);
  const colorsRef = useRef<ColorsStepHandle>(null);
  const logoRef = useRef<LogoStepHandle>(null);
  const referentRef = useRef<ReferentStepHandle>(null);
  const appearanceRef = useRef<AppearanceStepHandle>(null);
  const templateRef = useRef<TemplateStepHandle>(null);

  const steps = useMemo(() => ([
    { key: 'commercial-name', label: 'Nom commercial', ref: commercialNameRef },
    { key: 'logo', label: 'Sélection du logo', ref: logoRef },
    { key: 'email-subcontractor', label: 'Paramétrage du sous-traitant envoi des mails', ref: appearanceRef },
    { key: 'referent', label: 'Paramétrage du référent mutation location', ref: referentRef },
    { key: 'template', label: 'Choix du modèle de newsletter', ref: templateRef },
    { key: 'colors', label: 'Paramétrage des couleurs', ref: colorsRef },
    { key: 'report-upload', label: 'Présentation entreprise pour les rapports (optionnel)', ref: null },
    { key: 'summary', label: 'Récapitulatif', ref: null },
  ]), []);

  const totalSteps = steps.length;

  const stepQuery = searchParams.get('step') ?? '';

  useEffect(() => {
    if (!initialized || !company?.id) {
      return;
    }
    const shouldResolve =
      !appliedInitialStepResolution.current ||
      lastAppliedBrandingStepQuery.current !== stepQuery;
    if (!shouldResolve) {
      return;
    }
    appliedInitialStepResolution.current = true;
    lastAppliedBrandingStepQuery.current = stepQuery;

    const requested = stepQuery !== '' ? Number.parseInt(stepQuery, 10) : NaN;
    const hasNumericRequest = Number.isFinite(requested) && requested >= 0 && requested < totalSteps;
    const resolved = hasNumericRequest && canAccessBrandingWizardStep(company, requested)
      ? requested
      : firstIncompleteBrandingWizardStep(company);
    setActiveStep(Math.max(0, Math.min(resolved, totalSteps - 1)));
  }, [company, initialized, stepQuery, totalSteps]);

  const goToStep = (index: number) => {
    const clamped = Math.max(0, Math.min(index, totalSteps - 1));
    setActiveStep(clamped);
  };

  const validateAndCollectCurrent = useCallback((requireValid: boolean) => {
    const mapIndexToRef: Array<React.RefObject<any>> = [
      commercialNameRef,
      logoRef,
      appearanceRef,
      referentRef,
      templateRef,
      colorsRef,
    ];
    if (activeStep >= mapIndexToRef.length) { return { ok: true, partial: {} as Partial<BrandingDraft> }; }
    const handle = mapIndexToRef[activeStep]?.current;
    if (handle) {
      const ok = typeof handle.isValid === 'function' ? Boolean(handle.isValid()) : true;
      if (requireValid && !ok && typeof handle.focusFirstInvalid === 'function') {
        handle.focusFirstInvalid();
      }
      const partial = typeof handle.getPartialDraft === 'function' ? handle.getPartialDraft() : {};
      return { ok: requireValid ? ok : true, partial };
    }
    // If we require valid but the step handle isn't ready, use step-specific fallbacks
    if (requireValid && activeStep === 0) {
      // Step 0 is the Commercial Name step; always valid (optional field)
      return { ok: true, partial: {} };
    }
    if (requireValid && activeStep === 1) {
      // Step 1 is the Logo step (optional — matches LogoStep.isValid)
      return { ok: true, partial: {} };
    }
    if (requireValid && activeStep === 2) {
      // Step 2 is the Appearance step; valid if email subcontractor name is set in draft
      const ok = (draft.emailSubcontractorName || '').trim().length > 0;
      return { ok, partial: {} };
    }
    if (requireValid && activeStep === 3) {
      // Step 3 is the Referent step; require at least one referent with civilité and names; photo is optional
      const hasValidReferent = draft.referents && draft.referents.some(r => 
        r.title.trim().length > 0 && r.firstname.trim().length > 0 && r.lastname.trim().length > 0,
      );
      return { ok: Boolean(hasValidReferent), partial: {} };
    }
    if (requireValid && activeStep === 4) {
      // Step 4 is the Template step; valid if a template is already selected
      const ok = Boolean(draft.defaultTemplate || company?.brandingSettings?.defaultTemplate);
      return { ok, partial: {} };
    }
    if (requireValid && activeStep === 5) {
      // Step 5 is the Colors step; validate using current draft values
      const ok = isValidHex(draft.color1) && isValidHex(draft.color2) && isValidHex(draft.color3);
      return { ok, partial: {} };
    }
    if (activeStep === 6) {
      // Step 6 is the Report Upload step — always valid (optional)
      return { ok: true, partial: {} };
    }
    return { ok: requireValid ? false : true, partial: {} };
  }, [activeStep, company, draft]);

  const mergeDraft = useCallback((partial: Partial<BrandingDraft>) => {
    setDraft(prev => ({ ...prev, ...partial }));
  }, []);

  const handleBack = () => {
    goToStep(activeStep - 1);
  };

  const refreshCompanyFromApi = useCallback(async(): Promise<boolean> => {
    if (!company?.id || !jwt) { return false; }
    try {
      const response = await fetch(`/api/companies/${company.id}`, {
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (!response.ok) { return false; }
      const data = await response.json();
      dispatch(refreshCompanyData(data));
      return true;
    } catch (error) {
      console.error('Error refreshing company data:', error);
      return false;
    }
  }, [company?.id, dispatch, jwt]);

  const handleSaveDraft = async(returnAfter = false, silentSuccess = false): Promise<boolean> => {
    if (!initialized || !company?.id) { return false; }
    // Merge current step state into draft even if invalid (saving a draft)
    const { partial } = validateAndCollectCurrent(false);
    const mergedDraft: BrandingDraft = { ...draft, ...(partial || {}) } as BrandingDraft;
    setIsSaving(true);
    try {
      const base = company.brandingSettings || ({} as BrandingDraft);
      
      const updatedSettings: BrandingDraft = {
        color1: mergedDraft.color1 || base.color1 || DEFAULT_COLORS.color1,
        color2: mergedDraft.color2 || base.color2 || DEFAULT_COLORS.color2,
        color3: mergedDraft.color3 || base.color3 || DEFAULT_COLORS.color3,
        blogStructure: mergedDraft.blogStructure || base.blogStructure || 'straight-list',
        defaultTemplate: mergedDraft.defaultTemplate || base.defaultTemplate,
        // Multi-referent array — preserve report PDF paths not carried in the wizard draft
        referents: mergeReferentsPreservingPdfIntroduction(
          mergedDraft.referents || [],
          base.referents,
        ),
        emailSubcontractorName: mergedDraft.emailSubcontractorName || base.emailSubcontractorName || '',
        // Commercial name (optional - can be empty string)
        commercialName: mergedDraft.commercialName ?? base.commercialName ?? '',
        showLegalInfoOnBlog: mergedDraft.showLegalInfoOnBlog ?? base.showLegalInfoOnBlog ?? true,
      };

      const response = await fetch(`/api/companies/${company.id}/branding-settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify({ brandingSettings: updatedSettings }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error((errorData && (errorData.error || errorData.message)) || 'Failed to save branding settings');
      }

      dispatch(updateCompanyBranding(updatedSettings));
      // Sync local draft with saved values
      setDraft(updatedSettings);
      if (!silentSuccess) {
        showToast('success', returnAfter ? 'Brouillon sauvegardé.' : 'Personnalisation enregistrée avec succès.');
      }
      setHasChanges(false);
      if (returnAfter) {
        navigate(ROUTES.SETUP_SIMPLIFIED_DASHBOARD);
      }
      return true;
    } catch (e) {
      console.error(e);
      showToast('error', 'Échec de la sauvegarde des paramètres');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinish = async() => {
    setIsSaving(true);
    try {
      const success = await handleSaveDraft(false);
      if (success) {
        // Always return to setup dashboard when branding wizard is completed.
        navigate(ROUTES.SETUP_SIMPLIFIED_DASHBOARD);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async() => {
    const { ok, partial } = validateAndCollectCurrent(true);
    if (!ok) { return; }
    if (partial && Object.keys(partial).length > 0) {
      mergeDraft(partial);
    }
    const nextStep = activeStep + 1;
    // The report presentation step (index 6) uploads a PDF per referent using
    // the persisted BrandingReferent UUID. Referents edited/added earlier in the
    // wizard only live in local draft state, so we must persist them (backend +
    // Redux) before entering that step; otherwise new referents neither appear
    // in the list nor accept uploads (the API resolves referents by UUID).
    if (nextStep === REPORT_UPLOAD_STEP_INDEX) {
      const saved = await handleSaveDraft(false, true);
      if (!saved) { return; }
    }
    // Summary reads pdfIntroductionName from Redux; refresh after the upload step
    // so it matches the PDFs actually stored per referent on the server.
    if (activeStep === REPORT_UPLOAD_STEP_INDEX) {
      await refreshCompanyFromApi();
    }
    goToStep(nextStep);
  };

  const renderStep = () => {
    if (!initialized || !company?.id) {
      return (
        <div className="flex items-center justify-center rounded-lg bg-white p-8 shadow-md">
          <Spinner size="xl" />
        </div>
      );
    }
    switch (activeStep) {
      case 0:
        return (
          <CommercialNameStep
            ref={commercialNameRef}
            initial={{
              commercialName: draft.commercialName || '',
              showLegalInfoOnBlog: draft.showLegalInfoOnBlog ?? true,
            }}
            disabled={isSaving}
            onChange={mergeDraft}
          />
        );
      case 1:
        return (
          <LogoStep ref={logoRef} />
        );
      case 2:
        return (
          <AppearanceStep
            ref={appearanceRef}
            initial={{
              blogStructure: draft.blogStructure,
              emailSubcontractorName: draft.emailSubcontractorName || '',
            }}
            onChange={mergeDraft}
          />
        );
      case 3:
        return (
          <ReferentStep
            ref={referentRef}
            initial={{
              referents: draft.referents || [],
            }}
            onChange={mergeDraft}
          />
        );
      case 4:
        return (
          <TemplateStep ref={templateRef} />
        );
      case 5:
        return (
          <ColorsStep
            ref={colorsRef}
            initial={{ color1: draft.color1, color2: draft.color2, color3: draft.color3 }}
            onChange={mergeDraft}
          />
        );
      case 6:
        return (
          <div>
            <h3 className="mb-2 text-lg font-semibold text-gray-800">Présentation de l&apos;entreprise pour les rapports (optionnel)</h3>
            <p className="mb-4 text-sm text-gray-600">
              Cette étape est facultative : vous pouvez passer directement au récapitulatif.
            </p>
            <ReferentPresentationUploadList />
          </div>
        );
      default:
        return (
          <SummaryStep
            draft={draft}
            company={company}
            onEditReportPresentation={() => goToStep(6)}
          />
        );
    }
  };

  const canGoBack = activeStep > 0;
  const isLast = activeStep === totalSteps - 1;

  // Determine if current step is valid to enable Next
  const currentIsValid = () => {
    const mapIndexToRef: Array<React.RefObject<any>> = [
      commercialNameRef,
      logoRef,
      appearanceRef,
      referentRef,
      templateRef,
      colorsRef,
    ];
    if (activeStep >= mapIndexToRef.length) { return true; }
    const handle = mapIndexToRef[activeStep]?.current;

    // Compute step-specific validity using draft/company state so we don't depend solely on child handle timing
    let stepValid = false;
    switch (activeStep) {
      case 0:
        // Commercial Name step is always valid (optional field)
        stepValid = true;
        break;
      case 1:
        // Logo optional
        stepValid = true;
        break;
      case 2:
        stepValid = (draft.emailSubcontractorName || '').trim().length > 0;
        break;
      case 3:
        // At least one referent must have title, firstname, and lastname
        stepValid = Boolean(draft.referents && draft.referents.some(r => 
          r.title.trim().length > 0 && r.firstname.trim().length > 0 && r.lastname.trim().length > 0,
        ));
        break;
      case 4:
        stepValid = Boolean(draft.defaultTemplate || company?.brandingSettings?.defaultTemplate);
        break;
      case 5:
        stepValid = isValidHex(draft.color1) && isValidHex(draft.color2) && isValidHex(draft.color3);
        break;
      case 6:
        // Report upload is optional
        stepValid = true;
        break;
      default:
        stepValid = true;
    }

    const handleValid = handle && typeof handle.isValid === 'function' ? Boolean(handle.isValid()) : false;
    return stepValid || handleValid;
  };

  return (
    <div className="rounded-lg bg-white shadow-md">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900">Personnalisation de l'image de marque</h1>
        <p className="mt-2 text-gray-600">Complétez les étapes pour configurer l'apparence de vos communications.</p>
      </div>

      {/* Stepper */}
      <nav aria-label="Progress" className="px-6 pt-6 pb-4">
        <ol role="list" className="flex flex-wrap items-center gap-x-4 gap-y-4">
          {steps.map((s, idx) => {
            const isCurrent = activeStep === idx;
            const isCompleted = idx < activeStep;
            const canClick = idx <= activeStep;

            return (
              <li key={s.key} className="flex items-center">
                <button
                  type="button"
                  onClick={() => canClick && goToStep(idx)}
                  disabled={!canClick}
                  className={`group flex items-center ${!canClick ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  <span className={`
                    flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors
                    ${isCompleted ? 'border-green-600 bg-green-600 text-white' : ''}
                    ${isCurrent ? 'border-rgpd-blue bg-rgpd-blue text-white' : ''}
                    ${!isCompleted && !isCurrent ? 'border-gray-300 bg-white text-gray-500' : ''}
                  `}>
                    {isCompleted ? (
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <span>{idx + 1}</span>
                    )}
                  </span>
                  <span className={`ml-2 text-sm font-medium ${isCurrent ? 'text-rgpd-blue' : isCompleted ? 'text-green-700' : 'text-gray-600 group-hover:text-gray-900'}`}>
                    {s.label}
                  </span>
                </button>
                
                {idx < steps.length - 1 && (
                  <div className="ml-4 hidden h-0.5 w-8 rounded bg-gray-200 md:block" aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Body */}
      <div className="p-6">
        {renderStep()}
      </div>

      {/* Footer actions */}
      <div className="flex items-center gap-3 border-t border-gray-200 px-6 py-4">
        <button
          type="button"
          onClick={() => handleSaveDraft(true)}
          disabled={isSaving || !initialized}
          className="rounded-lg border border-gray-300 px-4 py-2 transition-colors hover:bg-gray-50 disabled:opacity-50"
        >
          {isSaving ? 'Enregistrement…' : 'Enregistrer & quitter'}
        </button>

        <div className="flex-1" />

        {canGoBack && (
          <button
            type="button"
            onClick={handleBack}
            className="rounded-lg border border-gray-300 px-4 py-2 transition-colors hover:bg-gray-50"
          >
            Précédent
          </button>
        )}

        {!isLast && (
          <button
            type="button"
            onClick={handleNext}
            disabled={!initialized || !currentIsValid()}
            className="rounded-lg bg-rgpd-blue px-4 py-2 text-white transition-colors hover:bg-rgpd-blue-dark disabled:opacity-50"
          >
            Suivant
          </button>
        )}

        {isLast && (
          <button
            type="button"
            onClick={handleFinish}
            disabled={isSaving || !initialized}
            className="rounded-lg bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700 disabled:opacity-50"
          >
            Confirmer et enregistrer
          </button>
        )}
      </div>
    </div>
  );
}

export default BrandingWizard;
